import angular from "angular";

import { OpenAPIV3 } from "@salaxy/core";

import { JsonSchemaUtils } from "@salaxy/core";

import { JsonSchemaService } from "../../services";
import { ColumnDefinition, Editor, EventCallBackMethods, Options, TabulatorFull as Tabulator } from "tabulator-tables";

/**
 * Configuration object for SxyTable:
 * Extends Tabulator Options with events and potentially some SxyTables specific options in the future.
 */
export interface SxyTableOptions extends Options {
  /** Event handlers that are added to Tabulator after it has been created. */
  events?: Partial<EventCallBackMethods>,
}

/**
 * Sxy form contains the definition for an editor used interface that
 * typically corresponds to an HTML form.
 */
export class SxyTableController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["JsonSchemaService", "$element", "$timeout"];

  /**
   * The schema definition that defines the structure of the grid.
   */
  public schemaDef?: OpenAPIV3.NonArraySchemaObject;

  /**
   * URL of the schema document that contains the schemaDef.
   * If set with schema in init, loads the document.
   */
  public doc?: string;

  /**
   * Key of schemaDef in doc.
   * If set with doc in init, will load the schema document from server and set the schemaDef.
   */
  public schema?: string;

  /**
   * Data that is rendered / edited in the grid.
   * This is either an array or an object that implements getTabulatorConfig() method for ajax data.
   */
  public data?: any[] | { getTabulatorConfig: () => Options };

  /**
   * List of columns to include in the grid.
   * This may be an array or comma separated string of field names (or a property tree, e.g. "owner.avatar.displayName").
   * Alternatively, the entries in the array may be be full Tabulator column definitions, where `field` propererty defines the
   * data item and fetches the default properties from the schema.
   */
  public include?: string | (string | ColumnDefinition)[];

  /**
   * List of columns to exclude from the grid.
   * This property is currenlty used only if include property is undefined, i.e. in default
   * rendering, which will render the first-level properties. As such, exclude does not really support property paths.
   */
  public exclude?: string | string[];

  /** Additional configuration for the table in init. */
  public options?: SxyTableOptions;

  /** After init, exposes the Tabulator for calling methods. */
  public tabulator?: Tabulator;

  /**
   * Gets the state of filters or sets the initial state of filters.
   * By default, the filters are not shown before showFilters() function is called.
   * If the property / attribute is set to true before initial render, the filters are shown.
   * Use showFilters() function to show or hide the filters after initial render.
   */
  public filters = false;

  /**
   * By default, the header filters are not shown before showFilters() function is called.
   * This is the place where the filter definitions are stored in the mean time.
   */
  public filterDefinitions: {
    [key: string]: Editor,
  } = {};

  private tableConfigCache = {
    data: null,
    include: null,
    exclude: null,
    options: null
  };

  constructor(
    private jsonSchemaService: JsonSchemaService,
    private $element: JQLite,
    protected $timeout: angular.ITimeoutService
  ) {}

  /** Initializes the controller */
  public $onInit() {
    this.init();
  }

  /** Intialises settings */
  public async init(): Promise<void> {
    this.doc = this.doc || "salaxy-secure/schemas/v02.json";
    if (!this.schemaDef && this.doc && this.schema) {
      const schemaDoc = await this.jsonSchemaService.assureSchemaDocument(this.doc);
      this.schemaDef = schemaDoc?.components?.schemas?.[this.schema] as OpenAPIV3.NonArraySchemaObject | undefined;
    }
    this.renderTable();
  }

  /**
   * Method used to refresh table if data, include, exclude or options changes
   */
  public refresh() {
    this.renderTable();
  }

  private async renderTable() {
    const columns: ColumnDefinition[] = [];
    this.getFields().forEach((colDefOrKey: string | ColumnDefinition) => {
      let key: string;
      let colDef: ColumnDefinition;
      if (typeof colDefOrKey === "string") {
        key = colDefOrKey;
        colDef = {
          field: key,
          title: "#field", // Will be replaced below
        };
      } else {
        key = colDefOrKey.field || "";
        colDef = colDefOrKey;
      }
      if (!key && !colDef.formatter) {
        console.error("Invalid column definition (no field): ", colDefOrKey);
        return;
      }
      const schema = JsonSchemaUtils.getProperty(this.schemaDef, key)?.schema;
      if (schema) {
        if (colDef == null || colDef.title == "#field") {
          colDef.title = schema.title || "#field";
        }
        this.setDefaultFormatter(schema, colDef);
        if (schema.description) {
          colDef.headerTooltip = schema.description;
        }
        if (schema.enum?.length) {
          // TODO: Add enum renderer.
          // const enumValues = SxyForms.i18n.getEnumMetadata(schema.format).values;
        }
      }
      if (colDef.title == null || colDef.title == "#field") {
        colDef.title = key.indexOf(".") ? key.substring(key.lastIndexOf(".") + 1) : key;
      }
      if (colDef.headerFilter) {
        if (colDef.field) {
          this.filterDefinitions[colDef.field] = colDef.headerFilter;
          if (!this.filters) {
            colDef.headerFilter = undefined;
          }
        } else {
          console.error("Cannot set filter for column without field: ", colDef);
        }
      }
      columns.push(colDef);
    });
    const tableOptions: SxyTableOptions = {};
    if (this.options) {
      // TODO: Potentially remove the SxyTable-specific options (events etc.) for future backward compatibility.
      Object.assign(tableOptions, this.options);
    }
    if ((this.data as any)?.getTabulatorConfig) {
      Object.assign(tableOptions, (this.data as any).getTabulatorConfig());
    } else {
      tableOptions.data = this.data as any[] || [];
    }
    if (columns.length) {
      tableOptions.columns = columns;
    } else if (tableOptions.autoColumns == null) {
      tableOptions.autoColumns = true;
    }
    const elem = this.$element.find("div")[0] as HTMLElement;
    if (!elem) {
      throw new Error("Cannot find div element for table.");
    }
    this.tabulator = new Tabulator(elem, tableOptions);
    const events = tableOptions.events ?? {};
    Object.keys(events).forEach((keyString: string) => {
      const key = keyString as keyof EventCallBackMethods;
      this.tabulator.on(key, events[key]);
    });

    await this.$timeout();
  }

  //TODO: Replaces @salaxy/core assureArray?
  private assureArray<T>(arrayCandidate: string | (T | string)[] | undefined): (T | string)[] {
    if (!arrayCandidate) {
      return [];
    }
    if (Array.isArray(arrayCandidate)) {
      return arrayCandidate;
    }
    return arrayCandidate.split(",");
  }

  private getFields(): (ColumnDefinition | string)[] {
    let fields = this.assureArray(this.include);
    if (fields.length == 0) {
      let keys = Object.keys(this.schemaDef?.properties || {});
      const excludeArr = this.assureArray(this.exclude);
      if (excludeArr.length > 0) {
        keys = keys.filter((x) => excludeArr.indexOf(x) < 0);
      }
      fields = keys;
    }
    return fields;
  }

  private setDefaultFormatter(schema: OpenAPIV3.SchemaObject, colDef: ColumnDefinition) {
    if (colDef.formatter) {
      return;
    }
    switch (schema.type) {
      case "integer":
      case "number":
        // TODO: Add number formatter.
        break;
      case "object":
        //TODO add avatar / other formatter
        break;
      case "string":
        if (schema.format === "date-time") {
          // HACK: Consider adding date time handling to utils
          colDef.formatter = (cell, _formatterParams, _onRendered) => {
            const value = cell.getValue();
            if (!value) {
              return "";
            }
            const date = new Date(value);
            return date.toLocaleDateString("fi-FI");
          };
          colDef.hozAlign = "right";
        }
    }
  }
}
